/**
 * "Use as event feed" (JSS#1182): the Production page links a cloud output's
 * source to `/audience/events?create=1&feed=<sourceId>&org=<orgId>`, which
 * opens Create Event with that feed and owner org set.
 *
 * The params are a hint from a link, not a choice the user made, so they are
 * checked before they reach the dialog, and a value that fails is dropped
 * without a word:
 *
 *   - `feed` must be a UUID (a `moq` source's id is its namespace).
 *   - `org` must be one of the user's orgs, or any org for a superuser. An org
 *     user's event is always their acting org's, so an `org` naming another
 *     org is ignored.
 *   - The feed is kept only when it belongs to the dialog's owner org: the
 *     source's own org when the sources list has it, else the link's `org`.
 *     A superuser with no valid `org` gets the source's org as owner, when the
 *     list knows it.
 *
 * event-manager still checks the namespace on create (checkProgrammeStream),
 * so none of this is a privilege boundary: it keeps a bad link from opening
 * a dialog that would be refused.
 */
import type { Source } from '@/types';

export interface CreateEventInitial {
  /** The owner org a superuser starts with. Ignored for an org user. */
  orgId?: string;
  moqNamespace?: string;
}

export const CREATE_PARAM = 'create';
export const FEED_PARAM = 'feed';
export const ORG_PARAM = 'org';

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function isUuid(value: string | null | undefined): value is string {
  return !!value && UUID_RE.test(value);
}

/** The Create Event link for a source, owned by `orgId` when known. */
export function eventFeedHref(sourceId: string, orgId: string | undefined | null): string {
  const params = new URLSearchParams({ [CREATE_PARAM]: '1', [FEED_PARAM]: sourceId });
  if (orgId) params.set(ORG_PARAM, orgId);
  return `/audience/events?${params.toString()}`;
}

export function resolveCreateEventPrefill(input: {
  feed: string | null;
  org: string | null;
  superuser: boolean;
  /** The orgs the user may act in (every org for a superuser); undefined if unread. */
  orgs: readonly { id: string }[] | undefined;
  actingOrgId: string | undefined;
  /** The caller's sources list; undefined if unread. */
  sources: readonly Pick<Source, 'id' | 'orgId'>[] | undefined;
}): CreateEventInitial {
  const { superuser, orgs, actingOrgId, sources } = input;
  const feed = isUuid(input.feed) ? input.feed : undefined;
  const known = (id: string | null | undefined): id is string => !!id && !!orgs?.some((o) => o.id === id);

  // An org user's `org` counts only when it is the org they act in.
  const paramOrg = superuser
    ? (known(input.org) ? input.org : undefined)
    : (input.org && input.org === actingOrgId && known(input.org) ? input.org : undefined);

  const source = feed ? sources?.find((s) => s.id === feed) : undefined;

  if (superuser) {
    const sourceOrg = source?.orgId ?? undefined;
    const owner = paramOrg ?? (known(sourceOrg) ? sourceOrg : undefined);
    if (!owner) return {};
    const keep = !!feed && (source?.orgId ? source.orgId === owner : paramOrg === owner);
    return keep ? { orgId: owner, moqNamespace: feed } : { orgId: owner };
  }

  // An org user's sources list is already their acting org's, so a source
  // it holds is theirs unless it names another org.
  const keep = !!feed && (source ? !source.orgId || source.orgId === actingOrgId : !!paramOrg);
  return keep ? { moqNamespace: feed } : {};
}
